import React, {forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState} from 'react';
import config from "../../app/config";
import API from "../../app/api";
import {setSnackbar} from "../../store/reducers/globals";
import {useDispatch, useSelector} from "react-redux";
import {currentMap, getBedasList} from '../../store/reducers/ward';
import {useTranslation} from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import {AgyleTable, createAgyleTableColumn} from "../../components/table/AgyleTable";
import _ from "lodash";
import moment from "moment";
import {Tooltip} from "@mui/material";
import useOpenState from "../../hooks/useOpenState";
import MessageBox from "../../util/messageBox";
import AgyleSwitchCalendar from "../../components/calendar/agyleSwitchCalendar";
import {BedasSessionsTable} from '../../components/calendar/calendarComponent/bedasSessionList';

const useStyles = makeStyles((theme) => ({
    referral: {
        display: "inline-block",
        width: 40,
        margin: 1,
        height: 30,
        textAlign: "center",
        lineHeight: "30px",
        color: "#fff",
        borderRadius: 10,
        fontSize: "small",
        "&.completed": {
            border: "2px solid #000",
            color: "#000",
            lineHeight: "25px",
        },
    },
}));

const columnsTable = [
    createAgyleTableColumn("id", "Hospital Number", 1, "id", "copyNumber", "", "medium", "left", true, true, 150, null),
    createAgyleTableColumn("name", "Name", 2, "name", "string", "", "medium", "left", true, true, null, 140),
    createAgyleTableColumn("date", "Date", 3, "date", "string", "", "medium", "left", true, true, 150, null),
    createAgyleTableColumn("dateRemain", "Time Remain", 4, "dateRemain", "countDown", "", "medium", "left", true, true, 115, null),
    createAgyleTableColumn("chiefComplain", "Chief Complain", 5, "chiefComplain", "string", "", "medium", "left", true, true, null, null),
    createAgyleTableColumn("reasons", "Reason", 6, "reason", "string", "", "medium", "left", true, true, null, null),
    createAgyleTableColumn("action", "Action", 7, null, "action", "notSort", "medium", "left", true, true, null, null),
];

const BedasList = forwardRef((props, ref) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bedasSessions = useSelector(getBedasList(props?.hospital?.hospitalId));

    // Table
    const [events, setEvents] = useState([]);
    const [dataTable, setDataTable] = useState([]);

    //Edit session reference
    const sessionRef = useRef(null);

    // Confirm cancel
    const [tempPt, setTempPt] = useState(null);
    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, false);
    const [isCalendarOpen, openCalendar, closeCalendar] = useOpenState(false, true);
    const [isSessionListOpen, openSessionList, closeSessionList] = useOpenState(false, true);
    const [isBlockModalOpen, openBlockModal, closeBlockModal] = useOpenState(false);

    const componentLabel = config?.appointmentNotBedasLabel ? 'Appointment' : 'Follow Up';

    useImperativeHandle(ref, () => ({
        openCalendar() {
            openCalendar();
        },
        openSessionList() {
            openSessionList();
        }
    }));

    useEffect(() => {
        const UPDATE_FREQUENCY = 10000; // 10 sec
        let interval;

        getBedasPatients();
        interval = setInterval(getBedasPatients, UPDATE_FREQUENCY);
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, []);

    useEffect(() => {
        const data = [];
        _.forEach(events, pt => {
            const bedasDate = moment(pt?.bedas?.date);

            const getCC = () => {
                if (pt.clerking?.chiefComplaint) return pt.clerking?.chiefComplaint?.__description;
                if (pt?.triage?.chiefComplaint) return pt?.triage?.chiefComplaint?.__description;
                if (pt?.admission?.initialAEComplaintDescription) return pt?.admission?.initialAEComplaintDescription;
                return "-";
            };

            data.push({
                id: pt?.patient?.__id,
                name: `${pt?.patient?.familyName} ${pt?.patient?.firstName}`,
                date: bedasDate.format(config.defaultDateTimeFormat),
                dateRemain: bedasDate.add(config?.modules?.bedas?.minutesRemain || 1080, "minutes"),
                chiefComplain: getCC(),
                reason: getReason(pt),
            });
        });
        setDataTable(data);
    }, [events]);

    const getReason = (patient) => {
        const reason = patient?.bedas?.reason;
        if (reason) {
            return <Tooltip title={reason}><div>{reason}</div></Tooltip>;
        } else {
            return "-"
        }
    }

    const getBedasPatients = () => {
        let wardIds = [];
        if (config.modules?.map?.customMap) wardIds.push(`ED##${props?.hospital?.hospitalId}`);
        else wardIds = ["ED", "0EDAU"];

        API.findPatientsInWard({wardIds, bedasStatus: "started"})
            .then(res => {
                if (res?.data?.__data) {
                    setEvents(res?.data?.__data);
                }
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: "error", message: "Bed status was not saved successfully!"}));
            });
    };

    const handleCancel = (e) => {
        setTempPt(e);
        openConfirm();
    };

    const handleCancelBedas = () => {
        const pt = _.find(events, p => p?.patient?.__id === tempPt?.id);
        if (pt) {
            API.updatePatientInWard(pt?.__id, [{"bedas.status": "canceled"}])
                .then(e => dispatch(setSnackbar({
                    type: "success",
                    message: `The booking was successfully archived.`,
                })))
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: "error",
                        message: "A problem occurred while cancel booking.",
                    }));
                }).finally(() => handleCloseConfirm());
        }
    };

    const handleCloseConfirm = () => {
        closeConfirm();
        setTempPt(null);
    };

    const handleCloseSessionModal = () => {
        closeBlockModal();
        sessionRef.current = null;
    }

    return (
        <>
            <AgyleTable
                columns={columnsTable}
                data={dataTable}
                deleteItem={handleCancel}
            />

            <MessageBox
                title={t(`Cancel ${componentLabel} Booking`)}
                haveAccept={{yes: t("Submit"), no: t("Close")}}
                warning
                onAccept={handleCancelBedas}
                onClose={handleCloseConfirm}
                message={t(`Are you sure you want to cancel this ${componentLabel} booking?`)}
                open={isConfirmOpen}
            />

            {config?.bedasSessionManagementEnabled && <BedasSessionsTable
                sessions={bedasSessions}
                open={isSessionListOpen}
                onClose={closeSessionList}
                disabled={props.editDisabled}
                onEdit={(session) => {
                    sessionRef.current = session;
                    openBlockModal();
                }}
                hospital={props?.hospital}
            />}

            <AgyleSwitchCalendar
                calendarType="bedas"
                events={events}
                bedasList
                open={isCalendarOpen}
                onClose={closeCalendar}
                onSessionManageOpen={openSessionList}
                sessionRef={sessionRef}
                openBlockModal={isBlockModalOpen}
                closeBlockModal={handleCloseSessionModal}
            />
        </>
    );
});

export default BedasList;